import { ref } from 'vue';
import billingAPI from '../api/omni/billing';

// Global state for plan limits modal
const limitModal = ref({
  show: false,
  resourceType: null,
  message: '',
  limit: 0,
  current: 0,
});

// Global state for plan expired modal (shown on dashboard)
const planExpiredModal = ref({
  show: false,
  data: null,
});

// Global state for resource reactivation modal
const reactivationModal = ref({
  show: false,
  data: null,
  selectedBots: [],
  selectedInboxes: [],
  selectedUsers: [],
});

export function usePlanLimits() {
  // Show the limit exceeded modal
  const showLimitExceededModal = ({ resourceType, message, limit, current }) => {
    limitModal.value = {
      show: true,
      resourceType,
      message,
      limit,
      current,
    };
  };

  // Hide the modal
  const hideLimitExceededModal = () => {
    limitModal.value.show = false;
  };

  // Check if API error is a limit exceeded error
  const isLimitExceededError = error => {
    return error?.response?.data?.error_code === 'LIMIT_EXCEEDED';
  };

  // Handle API error - shows modal if it's a limit exceeded error
  const handleApiError = error => {
    if (isLimitExceededError(error)) {
      const data = error.response.data;
      showLimitExceededModal({
        resourceType: data.resource_type,
        message: data.message,
        limit: data.limit,
        current: data.current,
      });
      return true;
    }
    return false;
  };

  // Get resource type label in Portuguese
  const getResourceLabel = resourceType => {
    const labels = {
      agents: 'Usuários',
      inboxes: 'Caixas de Entrada',
      bots: 'Atendentes IA',
    };
    return labels[resourceType] || resourceType;
  };

  // Fetch exceeding resources from API
  const fetchExceedingResources = async () => {
    try {
      const response = await billingAPI.getExceedingResources();
      return response.data;
    } catch (error) {
      console.error('Error fetching exceeding resources:', error);
      return null;
    }
  };

  // Fetch reenableable resources from API
  const fetchReenableableResources = async () => {
    try {
      const response = await billingAPI.getReenableableResources();
      return response.data;
    } catch (error) {
      console.error('Error fetching reenableable resources:', error);
      return null;
    }
  };

  // Re-enable selected resources
  const reenableSelectedResources = async selections => {
    try {
      const response = await billingAPI.reenableSelected(selections);
      return response.data;
    } catch (error) {
      console.error('Error re-enabling resources:', error);
      throw error;
    }
  };

  // Get plan status (for dashboard check)
  const fetchPlanStatus = async () => {
    try {
      const response = await billingAPI.getPlanStatus();
      return response.data;
    } catch (error) {
      console.error('Error fetching plan status:', error);
      return null;
    }
  };

  // Show plan expired modal
  const showPlanExpiredModal = data => {
    planExpiredModal.value = {
      show: true,
      data,
    };
  };

  // Hide plan expired modal
  const hidePlanExpiredModal = () => {
    planExpiredModal.value.show = false;
  };

  // Show reactivation modal
  const showReactivationModal = data => {
    reactivationModal.value = {
      show: true,
      data,
      selectedBots: [],
      selectedInboxes: [],
      selectedUsers: [],
    };
  };

  // Hide reactivation modal
  const hideReactivationModal = () => {
    reactivationModal.value.show = false;
  };

  return {
    limitModal,
    planExpiredModal,
    reactivationModal,
    showLimitExceededModal,
    hideLimitExceededModal,
    isLimitExceededError,
    handleApiError,
    getResourceLabel,
    fetchExceedingResources,
    fetchReenableableResources,
    reenableSelectedResources,
    fetchPlanStatus,
    showPlanExpiredModal,
    hidePlanExpiredModal,
    showReactivationModal,
    hideReactivationModal,
  };
}
